import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        // تحقق من وجود التوكن في localStorage
        const token = localStorage.getItem('admin_token');
        const adminData = localStorage.getItem('admin_data');
        const sessionData = localStorage.getItem('admin_session_v1');
        
        if (!token || !adminData || !sessionData) {
          setIsAuthenticated(false);
          setIsVerifying(false);
          return;
        }

        // ✅ التحقق من انتهاء صلاحية الجلسة المحلية
        try {
          const session = JSON.parse(sessionData);
          const loginTime = new Date(session.loggedInAt).getTime();
          const currentTime = Date.now();
          const SESSION_TIMEOUT = 3600000; // 1 hour

          if ((currentTime - loginTime) > SESSION_TIMEOUT) {
            console.warn('⏰ انتهت صلاحية الجلسة');
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_data');
            localStorage.removeItem('admin_session_v1');
            setIsAuthenticated(false);
            setIsVerifying(false);
            return;
          }
        } catch (error) {
          console.error('خطأ في قراءة بيانات الجلسة:', error);
          setIsAuthenticated(false);
          setIsVerifying(false);
          return;
        }

        // تحقق من صلاحية الجلسة مع Supabase
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          console.error('❌ خطأ في جلسة Supabase:', error);
          // الجلسة انتهت، امسح البيانات المحلية
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_data');
          localStorage.removeItem('admin_session_v1');
          setIsAuthenticated(false);
          setIsVerifying(false);
          return;
        }

        // ✅ التحقق من أن المستخدم لا يزال مديراً نشطاً
        const { data: admin, error: adminError } = await supabase
          .from('admins')
          .select('id, is_active')
          .eq('auth_user_id', session.user.id)
          .eq('is_active', true)
          .maybeSingle();

        if (adminError || !admin) {
          console.error('❌ المدير غير موجود أو غير نشط');
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_data');
          localStorage.removeItem('admin_session_v1');
          await supabase.auth.signOut();
          setIsAuthenticated(false);
          setIsVerifying(false);
          return;
        }

        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error verifying session:', error);
        setIsAuthenticated(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyAuth();

    // ✅ إعداد مستمع لتغييرات حالة المصادقة
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_data');
        localStorage.removeItem('admin_session_v1');
        setIsAuthenticated(false);
      }
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0c343d] to-[#0f3f4a] flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#f9bf4b] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">جاري التحقق من الجلسة...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}